import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { fetchImages, deleteImage, uploadImages } from './api';
import Header from './Header';
import FilmStrip from './FilmStrip';

// Společný tmavý vzhled pro všechny SweetAlert2 dialogy, aby ladily s aplikací.
const swalDark = {
  background: '#171310',
  color: '#f0e9df',
  confirmButtonColor: '#e13a2e',
  cancelButtonColor: '#3a2f26',
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const d = new Date(timestamp * 1000);
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Gallery({ onBack, onLogout }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const fileInputRef = useRef(null);

  const loadImages = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchImages();
      if (!data.success) throw new Error(data.message || 'Načtení se nezdařilo.');
      setImages(data.images || []);
    } catch (err) {
      setLoadError(err.message || 'Nepodařilo se spojit se serverem.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const handleDelete = async (image) => {
    const result = await Swal.fire({
      ...swalDark,
      title: 'Smazat fotku?',
      html: `Opravdu chceš natrvalo smazat <strong>${image.name}</strong>?<br/>Tuto akci nelze vrátit zpět.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Smazat',
      cancelButtonText: 'Zrušit',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteImage(image.name);
      if (!res.success) throw new Error(res.message || 'Smazání se nezdařilo.');
      setImages((prev) => prev.filter((i) => i.name !== image.name));
      Swal.fire({
        ...swalDark,
        title: 'Smazáno',
        icon: 'success',
        timer: 1300,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Chyba',
        text: err.message || 'Smazání se nezdařilo.',
        icon: 'error',
      });
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;

    setUploading(true);
    try {
      const res = await uploadImages(files);

      if (res.uploaded && res.uploaded.length) {
        await loadImages();
      }

      if (res.errors && res.errors.length) {
        Swal.fire({
          ...swalDark,
          title: res.uploaded.length ? 'Nahráno s výhradami' : 'Nahrávání selhalo',
          html: res.errors.map((e) => `• ${e}`).join('<br/>'),
          icon: 'warning',
          confirmButtonColor: '#d9932f',
        });
      } else if (res.uploaded && res.uploaded.length) {
        Swal.fire({
          ...swalDark,
          title: 'Vyvoláno!',
          text: `Do galerie přibylo ${res.uploaded.length} ${res.uploaded.length === 1 ? 'fotografie' : 'fotografií'}.`,
          icon: 'success',
          timer: 1600,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Chyba',
        text: err.message || 'Nahrávání se nezdařilo.',
        icon: 'error',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onInputChange = (e) => handleFiles(e.target.files);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  return (
    <div className="page">
      <Header
        eyebrow="Kontaktní arch"
        title="Galerie"
        onBack={onBack}
        onLogout={onLogout}
        right={
          <div className="header__count">
            <span className="header__count-number">{images.length}</span>
            <span className="header__count-label">
              {images.length === 1 ? 'fotka' : images.length >= 2 && images.length <= 4 ? 'fotky' : 'fotek'}
            </span>
          </div>
        }
      />

      <FilmStrip />

      <main className="main">
        <section
          className={`dropzone ${dragActive ? 'dropzone--active' : ''} ${uploading ? 'dropzone--busy' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !uploading) fileInputRef.current?.click();
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onInputChange}
            className="dropzone__input"
            disabled={uploading}
          />
          <div className="dropzone__icon">{uploading ? '◍' : '▤'}</div>
          <p className="dropzone__title">
            {uploading ? 'Vyvolávám film…' : 'Přetáhni fotky sem, nebo klikni pro výběr'}
          </p>
          <p className="dropzone__hint">Podporuje výběr více souborů najednou · JPG, PNG, GIF, WEBP, BMP</p>
        </section>

        {loadError && (
          <div className="state state--error">
            <p>{loadError}</p>
            <button className="btn btn--ghost" onClick={loadImages}>Zkusit znovu</button>
          </div>
        )}

        {!loadError && loading && (
          <div className="state">
            <p>Prohlížím archiv…</p>
          </div>
        )}

        {!loadError && !loading && images.length === 0 && (
          <div className="state">
            <p>Ve složce zatím nejsou žádné fotografie.</p>
            <p className="state__sub">Nahraj první snímek výše a začni budovat sbírku.</p>
          </div>
        )}

        {!loadError && !loading && images.length > 0 && (
          <div className="grid">
            {images.map((image) => (
              <article className="frame" key={image.name}>
                <FilmStrip holes={6} />
                <div className="frame__photo">
                  <img src={image.url} alt={image.name} loading="lazy" />
                </div>
                <FilmStrip holes={6} />
                <div className="frame__meta">
                  <p className="frame__name" title={image.name}>{image.name}</p>
                  <p className="frame__sub">{formatBytes(image.size)} · {formatDate(image.modified)}</p>
                </div>
                <button className="frame__delete" onClick={() => handleDelete(image)}>
                  Smazat
                </button>
              </article>
            ))}
          </div>
        )}
      </main>

      <footer className="footer">
        <span className="footer__dot" /> Připojeno k backendu
      </footer>
    </div>
  );
}
